import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Check } from 'lucide-react';
import PageBanner from '../components/common/PageBanner';
import { Section, Head, Index } from '../components/common/Primitives';
import { guaranteeRegister, closingRules, systemMeta } from '../data/system';
import banner from '../assets/banners/components.jpg';

/* The claim "it runs on the farm" is only worth making if every service that
   would ordinarily be somebody else's cloud can be named and placed. What each
   one is built from is not the interesting part; where it runs is. */
const onSite = [
  'The network server the field nodes join',
  'The message bus carrying readings out and commands back',
  'The record every reading and every action is written to',
  'The decision model that sizes a dose',
  'The safety engine that authorises it',
  'The knowledge base, and the assistant that reads it',
];

const Components = () => (
  <div>
    <PageBanner
      image={banner}
      kicker="Farm Tech · Traceability"
      title="What it guarantees"
      lead="Every layer of this system exists to promise something specific. These are those promises, and for each one, how a failure would make itself visible rather than pass unnoticed."
    />

    <Section tone="paper">
      <Head
        eyebrow="Guarantee register"
        title="Eight promises, and how you would know"
        lead="A guarantee nobody can check is just a claim. So each row pairs what the layer promises with the thing that would surface if it stopped being true, because the second column is what makes the first one worth anything."
      />

      <div className="mt-12 overflow-x-auto">
        <table className="w-full border-collapse min-w-[46rem]">
          <thead>
            <tr>
              <th className="label text-left text-paper bg-ink px-6 py-4 rounded-tl-xl w-[13rem]">
                Layer
              </th>
              <th className="label text-left text-lime bg-ink px-6 py-4 w-[20rem]">
                What it guarantees
              </th>
              <th className="label text-left text-paper/60 bg-ink px-6 py-4 rounded-tr-xl">
                How a failure would show
              </th>
            </tr>
          </thead>
          <tbody>
            {guaranteeRegister.map((row, i) => (
              <tr key={row.layer} className={i % 2 ? 'bg-chalk' : 'bg-paper2/60'}>
                <td className="px-6 py-4 align-top font-medium text-ink text-[14.5px]">
                  {row.layer}
                </td>
                <td className="px-6 py-4 align-top font-medium text-forest text-[14px] leading-relaxed">
                  {row.guarantee}
                </td>
                <td className="px-6 py-4 align-top text-soil/70 text-[14px] leading-relaxed">
                  {row.evidence}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="mt-6 text-soil/55 text-[13.5px]">
        Scroll the table sideways on a narrow screen; the third column is the point of it.
      </p>
    </Section>

    {/* Offline-first, named service by service rather than illustrated */}
    <Section tone="soil">
      <div className="grid lg:grid-cols-2 gap-x-14 gap-y-10 items-start">
        <Head
          tone="dark"
          eyebrow="Why it holds together"
          title="Every service runs on the farm"
          lead="There is no vendor cloud anywhere in the actuation path. That is what lets the control loop keep running when the link drops. It is a claim worth checking, so every service that could have been somebody else's is listed here."
        />
        <div>
          <p className="label text-lime">On site · not in a vendor cloud</p>
          <ul className="mt-6">
            {onSite.map((s) => (
              <li key={s} className="flex items-start gap-3.5 py-3.5 border-b border-white/10">
                <Check className="w-4 h-4 text-lime mt-0.5 shrink-0" />
                <span className="text-paper/70 text-[14.5px] leading-relaxed">{s}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </Section>

    {/* Closing rules */}
    <Section tone="ink">
      <Head tone="dark" eyebrow="The design, in one rule" title={systemMeta.rule} center />
      <div className="mt-12 grid md:grid-cols-2 gap-4 max-w-5xl mx-auto">
        {closingRules.map((r, i) => (
          <div key={r} className="flex items-start gap-4 rounded-lg bg-white/[0.04] ring-1 ring-white/10 p-5">
            <Index n={i + 1} tone="dark" />
            <p className="text-paper/75 text-[14.5px] leading-relaxed pt-1">{r}</p>
          </div>
        ))}
      </div>
      <div className="mt-12 text-center">
        <Link
          to="/farm-tech"
          className="inline-flex items-center gap-2 text-lime hover:text-paper font-medium transition-colors"
        >
          Back to how we grow
          <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </Section>
  </div>
);

export default Components;
